import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { FormField } from "@/components/ui/form-field";
import { Input } from "@/components/ui/input";
import { Banner } from "@/components/ui/banner";
import { legacySignUpSchema, type LegacySignUpValues } from "@/routes/legacy-signup/legacy-signup-schema";
import { toLegacySignUpError } from "@/routes/legacy-signup/legacy-signup-error";
import { useLegalDocument, type LegalDocumentType } from "@/routes/legacy-signup/useLegalDocument";
import type { AuthBanner } from "@/routes/sign-in/auth-error";

// Legacy clinician sign-up (UX Screen 00). Previously-certified AMPS clinicians do
// not pass through LearnWorlds — they create an account here, sign in, then upload
// their prior-AMPS certificate from the Profile tab for manual CITS verification.
//
// Flow: POST /auth/register-legacy { first_name, last_name, email,
// prior_certification_confirmed } → 202 Accepted (NO password sent or returned; NO
// Stripe opened — FR39 ordering) → route back to /sign-in with a one-time notice
// taken from the API `message`. New and duplicate addresses share that hedged
// acknowledgement (S-C-01), so we never claim an account was created or that
// credentials were emailed. We never auto-login: webapp_legacy users are
// is_active=false until super_admin verification.
//
// skipAuthRefresh: true — this is genuinely pre-session. No secret is ever entered
// here (the server generates + emails the temporary password).

// Story 23.2 (FR89): self-declared association membership options.
const ASSOCIATION_OPTIONS = [
  { value: "otbc", label: "OTBC member" },
  { value: "saot", label: "SAOT member" },
  { value: "osot", label: "OSOT member" },
  { value: "none", label: "None" },
] as const;

// Shape of the unwrapped `data` object: apiFetch strips the { status, data }
// envelope and returns `data` (see api-client unwrap). S-C-01: new and duplicate
// sign-ups return the same generic acknowledgement, so this is a message, not
// a user record. Identity never rides this response.
interface RegisterLegacyResponse {
  message: string;
}

// The three hosted CITS documents the acknowledgement covers (Story 16.8, AC1).
// Each gets its OWN link: a single "legal documents" link would leave a clinician
// unable to tell which document they are opening, and the acknowledgement is
// recorded per document. Order and titles match the catalogue rows the server
// resolves the recorded version from.
const LEGAL_DOCUMENTS: ReadonlyArray<{ type: LegalDocumentType; title: string }> = [
  { type: "dpa", title: "Data Processing Agreement" },
  { type: "terms_of_use", title: "Terms of Use" },
  { type: "privacy_policy", title: "Privacy Policy" },
];

const DOCUMENT_UNAVAILABLE_MESSAGE =
  "That document can't be opened right now. You can still create your account — the acknowledgement is recorded either way.";

// The three required acknowledgements, in render order, each paired with the
// heading printed beside its checkbox — so a blocked-submit reason can name the
// control the clinician has to go back to, in the words already on the screen.
const ACKNOWLEDGEMENT_FIELDS = [
  { name: "prior_certification_confirmed", label: "Prior AMPS certification" },
  { name: "document_acknowledgement_confirmed", label: "Legal documents" },
  { name: "data_responsibility_confirmed", label: "Client data responsibility" },
] as const;

/** "a", "a and b", "a, b and c" — the acknowledgement names in a readable list. */
function joinLabels(labels: readonly string[]): string {
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/**
 * Why the primary CTA is refusing, or null when it is not (PR #741 review).
 *
 * The CTA used to be NATIVELY disabled with no reason attached anywhere. Native
 * `disabled` removes a control from the tab order, and `mode: "onChange"` renders
 * no error text until a field has been touched — so a screen-reader user who
 * tabbed past the three acknowledgement checkboxes reached a form with no submit
 * control and nothing saying why. The house pattern for a refusing CTA is
 * `aria-disabled` + a `title` naming the block (`AccessReadinessCard`, and
 * `components/ui/button.tsx` keeps such buttons hit-testable precisely so the
 * tooltip can be delivered); this is that reason string.
 *
 * Pure and exported so the wording is pinned by a unit test rather than only by a
 * render assertion.
 */
export function submitBlockedReason(input: {
  isValid: boolean;
  outstandingAcknowledgements: readonly string[];
}): string | null {
  if (input.isValid) return null;
  if (input.outstandingAcknowledgements.length === 0) {
    return "Please complete the required fields above.";
  }
  return `Please tick ${joinLabels(
    input.outstandingAcknowledgements,
  )} before creating your account.`;
}

export function LegacySignUpScreen() {
  const navigate = useNavigate();
  const [banner, setBanner] = React.useState<AuthBanner | null>(null);

  const form = useForm<LegacySignUpValues>({
    resolver: zodResolver(legacySignUpSchema),
    // onChange so formState.isValid tracks live — the primary CTA stays disabled
    // until every required field is valid AND the attestation checkbox is ticked.
    mode: "onChange",
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      prior_certification_confirmed: false,
      document_acknowledgement_confirmed: false,
      data_responsibility_confirmed: false,
      association: "none",
    },
  });

  // One mutation shared by all three document links — a click mints a FRESH
  // short-TTL pre-signed URL. `openedType` tracks which link is minting so only
  // that one shows its pending state.
  const [openedType, setOpenedType] = React.useState<LegalDocumentType | null>(null);
  const [documentError, setDocumentError] = React.useState<string | null>(null);
  const legalDocument = useLegalDocument();

  const openLegalDocument = (documentType: LegalDocumentType) => {
    if (legalDocument.isPending) return;
    setDocumentError(null);
    setOpenedType(documentType);
    // Popup-blocker gotcha, the same one `useRenewNow` documents: `window.open`
    // called after an awaited fetch falls outside the user-gesture window and is
    // blocked in Safari/Chrome, so the tab is opened SYNCHRONOUSLY here inside
    // the click and navigated once the URL resolves. The `"noopener"` FEATURE
    // cannot be used — passing it makes `window.open` return null BY SPEC, which
    // would both defeat the deferred navigation and make a genuine block
    // undetectable — so the opener link is severed by hand instead (same
    // isolation: the S3 origin cannot reach back through window.opener).
    const tab = window.open("", "_blank");
    if (tab) tab.opener = null;
    legalDocument.mutate(documentType, {
      onSuccess: (data) => {
        if (tab && !tab.closed) {
          tab.location.href = data.download_url;
          return;
        }
        // Blocked, or the clinician closed the placeholder mid-flight. This does
        // NOT fall back to same-tab navigation: the form is half-filled and
        // navigating away would discard everything typed so far, which is the one
        // degradation this screen cannot take. Surfacing the non-blocking notice
        // is the honest answer — sign-up still works either way.
        setDocumentError(DOCUMENT_UNAVAILABLE_MESSAGE);
      },
      // The document store may not be wired yet (LEGAL-503) or the row may carry
      // no hosted copy (LEGAL-404). Either way this is NON-BLOCKING: sign-up
      // still works, because the acknowledgement is recorded against the
      // catalogue row, not against a reachable object.
      onError: () => {
        if (tab && !tab.closed) tab.close();
        setDocumentError(DOCUMENT_UNAVAILABLE_MESSAGE);
      },
      onSettled: () => setOpenedType(null),
    });
  };

  const registerMutation = useMutation({
    mutationFn: (values: LegacySignUpValues) =>
      apiFetch<RegisterLegacyResponse>("/auth/register-legacy", {
        method: "POST",
        body: {
          first_name: values.first_name,
          last_name: values.last_name,
          email: values.email,
          prior_certification_confirmed: values.prior_certification_confirmed,
          document_acknowledgement_confirmed: values.document_acknowledgement_confirmed,
          data_responsibility_confirmed: values.data_responsibility_confirmed,
          association: values.association,
        },
        skipAuthRefresh: true,
      }),
    onSuccess: (data) => {
      setBanner(null);
      // No token is issued; land back on sign-in with the API acknowledgement —
      // NOT a Stripe redirect, and not a stronger claim than the server made.
      navigate("/sign-in", {
        replace: true,
        state: { registered: true, registeredNotice: data.message },
      });
    },
    onError: (error) => {
      // AUTH-003 is not a designed path (duplicate email is a 202 acknowledgement).
      // If it still arrives, toLegacySignUpError maps it to a generic banner — never
      // an inline "account already exists" field error.
      setBanner(toLegacySignUpError(error).banner);
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    if (registerMutation.isPending) return;
    setBanner(null);
    registerMutation.mutate(values);
  });

  // `handleSubmit` above is the actual gate: an invalid submit never reaches the
  // mutation, and it marks the fields touched so their error text renders — which
  // is why the CTA below may stay clickable and merely ANNOUNCE the block. A
  // press by somebody who could not see the reason is what surfaces it.
  const acknowledged = form.watch(ACKNOWLEDGEMENT_FIELDS.map((f) => f.name));
  const blockedReason = submitBlockedReason({
    isValid: form.formState.isValid,
    outstandingAcknowledgements: ACKNOWLEDGEMENT_FIELDS.filter(
      (_field, index) => !acknowledged[index],
    ).map((field) => field.label),
  });

  return (
    <main className="flex min-h-screen items-center justify-center p-5">
      <div className="grid w-full max-w-[min(var(--width-sm),100%)] items-stretch gap-10 lg:max-w-[1120px] lg:grid-cols-[1.05fr_0.95fr]">
        {/* Hero panel — decorative, hidden on small viewports (the auth card is the
            required element; the hero is optional/responsive per the mockup). */}
        <section
          className="hidden flex-col justify-center gap-5 rounded-3xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] p-14 lg:flex"
          aria-hidden="true"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[color:var(--bg-brand-solid)] font-semibold text-[color:var(--text-white)]">
              AM
            </div>
            <div className="flex flex-col leading-tight">
              <strong className="text-[color:var(--text-primary)]">CITS AMPS</strong>
              <span className="text-sm text-[color:var(--text-tertiary)]">Clinical scoring platform</span>
            </div>
          </div>
          <p className="display-xs text-[color:var(--text-primary)]">Already AMPS-certified?</p>
          <p className="text-md text-[color:var(--text-secondary)]">
            Bring your existing AMPS certification into the new platform. Create your account
            here, sign in, then upload your prior-AMPS certificate from the Profile tab. A CITS
            admin verifies it manually — once verified, you complete checkout and your account
            unlocks for calibration and live scoring.
          </p>
        </section>

        <Card className="w-full shadow-elevated">
          <CardHeader>
            <CardTitle>Create legacy account</CardTitle>
            <CardDescription>
              Fields marked * are required. You will set your password on first sign-in after
              admin verification.
            </CardDescription>
          </CardHeader>

          {banner ? (
            <Banner variant={banner.variant} className="mt-4">
              {banner.message}
            </Banner>
          ) : null}

          <form className="mt-5 flex flex-col gap-4" onSubmit={onSubmit} noValidate>
            <FormField label="First name" error={form.formState.errors.first_name?.message} required>
              <Input autoComplete="given-name" {...form.register("first_name")} />
            </FormField>

            <FormField label="Last name" error={form.formState.errors.last_name?.message} required>
              <Input autoComplete="family-name" {...form.register("last_name")} />
            </FormField>

            <FormField label="Work email" error={form.formState.errors.email?.message} required>
              <Input type="email" autoComplete="email" {...form.register("email")} />
            </FormField>

            {/* Association membership declaration (Story 23.2, FR89).
                fieldset+legend — native grouping; keyboard arrow-key cycle; WCAG 2.1 SC 1.3.1.
                Optional field: does not block submit. */}
            <fieldset className="flex flex-col gap-2">
              <legend className="text-sm font-medium text-[color:var(--text-primary)]">
                Association membership
              </legend>
              <p className="text-sm text-[color:var(--text-secondary)]">
                I declare I am a current member
              </p>
              <div className="flex flex-col gap-2 pt-1">
                {ASSOCIATION_OPTIONS.map((option) => (
                  <label
                    key={option.value}
                    className="flex cursor-pointer items-center gap-2.5 text-sm text-[color:var(--text-secondary)]"
                  >
                    <input
                      type="radio"
                      value={option.value}
                      className="h-4 w-4 accent-[color:var(--bg-brand-solid)]"
                      {...form.register("association")}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </fieldset>

            {/* Prior-AMPS-certification attestation. Required (Zod refine true): the
                CTA stays disabled until this is checked. */}
            <div className="grid content-start gap-[6px]">
              <div className="flex items-start gap-3">
                <Controller
                  control={form.control}
                  name="prior_certification_confirmed"
                  render={({ field }) => (
                    <Checkbox
                      id="prior-certification-confirmed"
                      ref={field.ref}
                      className="mt-[3px]"
                      checked={field.value}
                      onBlur={field.onBlur}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                      aria-invalid={form.formState.errors.prior_certification_confirmed ? true : undefined}
                    />
                  )}
                />
                <label
                  htmlFor="prior-certification-confirmed"
                  className="text-sm text-[color:var(--text-secondary)]"
                >
                  <span className="font-medium text-[color:var(--text-primary)]">
                    Prior AMPS certification{" "}
                    <span aria-hidden="true">*</span>
                    <span className="sr-only"> (required)</span>
                  </span>
                  <br />
                  I confirm that I have completed AMPS training and hold a valid prior-AMPS
                  certificate. I will upload the certificate from the Profile tab after sign-up.
                </label>
              </div>
              {form.formState.errors.prior_certification_confirmed ? (
                <p role="alert" className="text-sm text-[color:var(--text-error)]">
                  {form.formState.errors.prior_certification_confirmed.message}
                </p>
              ) : null}
            </div>

            {/* Document acknowledgement (Story 16.8, AC1/AC2). The copy is CITS
                legal text — render it verbatim, do not reword. Each named
                document carries its own link so the clinician can READ what they
                are accepting before they accept it. */}
            <div className="grid content-start gap-[6px]">
              <div className="flex items-start gap-3">
                <Controller
                  control={form.control}
                  name="document_acknowledgement_confirmed"
                  render={({ field }) => (
                    <Checkbox
                      id="document-acknowledgement-confirmed"
                      ref={field.ref}
                      className="mt-[3px]"
                      checked={field.value}
                      onBlur={field.onBlur}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                      aria-invalid={
                        form.formState.errors.document_acknowledgement_confirmed ? true : undefined
                      }
                    />
                  )}
                />
                <label
                  htmlFor="document-acknowledgement-confirmed"
                  className="text-sm text-[color:var(--text-secondary)]"
                >
                  <span className="font-medium text-[color:var(--text-primary)]">
                    Legal documents{" "}
                    <span aria-hidden="true">*</span>
                    <span className="sr-only"> (required)</span>
                  </span>
                  <br />
                  I acknowledge and agree to the CITS Data Processing Agreement, Terms of Use,
                  and Privacy Policy.
                </label>
              </div>

              {/* Outside the label: a control inside a label would toggle the
                  checkbox on click as well as opening the document. */}
              <div className="ml-8 flex flex-wrap items-center gap-x-3 gap-y-1">
                {LEGAL_DOCUMENTS.map((document) => (
                  // No `disabled` here (PR #741 review). It was set from the ONE
                  // shared mutation, so a single in-flight mint disabled all three
                  // links — and, worse, disabled the very button being activated:
                  // the browser drops focus from an element that becomes disabled
                  // under it, so a keyboard user's focus fell to <body> at the
                  // moment of the click. Nothing was bought for that: re-entrancy
                  // is already refused in `openLegalDocument` above, which returns
                  // before `window.open` while a mint is in flight, so the
                  // attribute never was what prevented a double mint.
                  //
                  // `aria-busy` carries the pending state to assistive tech, the
                  // label swap carries it visually, and focus stays where the
                  // clinician put it.
                  <Button
                    key={document.type}
                    type="button"
                    variant="link"
                    size="sm"
                    className="h-auto p-0"
                    aria-busy={openedType === document.type}
                    onClick={() => openLegalDocument(document.type)}
                  >
                    {openedType === document.type ? `Opening ${document.title}…` : document.title}
                  </Button>
                ))}
              </div>
              {documentError ? (
                <p role="alert" className="ml-8 text-sm text-[color:var(--text-error)]">
                  {documentError}
                </p>
              ) : null}

              {form.formState.errors.document_acknowledgement_confirmed ? (
                <p role="alert" className="text-sm text-[color:var(--text-error)]">
                  {form.formState.errors.document_acknowledgement_confirmed.message}
                </p>
              ) : null}
            </div>

            {/* Data-responsibility acknowledgement (Story 16.8, AC1/AC2). A
                statement the clinician makes about their OWN duty — no hosted
                document backs it, which is why it has no link. Verbatim copy. */}
            <div className="grid content-start gap-[6px]">
              <div className="flex items-start gap-3">
                <Controller
                  control={form.control}
                  name="data_responsibility_confirmed"
                  render={({ field }) => (
                    <Checkbox
                      id="data-responsibility-confirmed"
                      ref={field.ref}
                      className="mt-[3px]"
                      checked={field.value}
                      onBlur={field.onBlur}
                      onCheckedChange={(checked) => field.onChange(checked === true)}
                      aria-invalid={
                        form.formState.errors.data_responsibility_confirmed ? true : undefined
                      }
                    />
                  )}
                />
                <label
                  htmlFor="data-responsibility-confirmed"
                  className="text-sm text-[color:var(--text-secondary)]"
                >
                  <span className="font-medium text-[color:var(--text-primary)]">
                    Client data responsibility{" "}
                    <span aria-hidden="true">*</span>
                    <span className="sr-only"> (required)</span>
                  </span>
                  <br />
                  I understand that I am responsible for entering only the minimum necessary
                  client/service-user information and for complying with applicable privacy,
                  consent, and data-protection requirements within my jurisdiction and
                  organization.
                </label>
              </div>
              {form.formState.errors.data_responsibility_confirmed ? (
                <p role="alert" className="text-sm text-[color:var(--text-error)]">
                  {form.formState.errors.data_responsibility_confirmed.message}
                </p>
              ) : null}
            </div>

            <Banner variant="info" title="What happens next">
              The platform creates your account in a disabled state and emails you sign-in
              details. You sign in, upload your AMPS certificate from the Profile tab, and wait
              for a CITS admin to verify it. After verification you complete checkout for annual
              access; only then is your account enabled and you're routed into calibration.
            </Banner>

            {/* `aria-disabled`, not native `disabled`, for the INCOMPLETE-form
                block: the control stays in the tab order and carries its reason,
                and pressing it runs `handleSubmit`, which refuses the submit and
                renders the field errors. Native `disabled` is kept for the
                IN-FLIGHT state alone — that one is a true "cannot act right now",
                it is momentary, and `aria-busy` explains it. */}
            <Button
              type="submit"
              variant="primary"
              className="aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
              disabled={registerMutation.isPending}
              aria-busy={registerMutation.isPending}
              aria-disabled={blockedReason !== null}
              title={blockedReason ?? undefined}
            >
              {registerMutation.isPending ? "Creating account…" : "Create account"}
            </Button>

            <p className="mt-1 text-center text-sm text-[color:var(--text-tertiary)]">
              <Button asChild variant="link">
                <Link to="/sign-in">Back to sign in</Link>
              </Button>
            </p>
          </form>
        </Card>
      </div>
    </main>
  );
}

export default LegacySignUpScreen;
